import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Calculator, Package, FileText } from 'lucide-react';
import { sanitizeEstimateDescription, getDisplayNotes } from '../../lib/estimate-notes';

interface EstimateItem {
  id?: number;
  serviceType?: string;
  service_type?: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  amount: number;
  notes?: string;
}

/** Format saved service type for display (e.g. "roofing" -> "Roofing"). Use saved value, never "Custom" when we have a value. */
function formatServiceLabel(item: EstimateItem): string {
  const raw = item.serviceType ?? item.service_type ?? '';
  if (!raw || raw === 'Custom') return 'Custom Service';
  const lower = raw.toLowerCase();
  const titleCase = lower.charAt(0).toUpperCase() + lower.slice(1);
  const known: Record<string, string> = {
    roofing: 'Roofing',
    roof: 'Roofing',
    fence: 'Fence',
    deck: 'Deck',
    windows: 'Windows',
    gutters: 'Gutters',
    siding: 'Siding',
    flooring: 'Flooring',
    painting: 'Painting',
    electrical: 'Electrical',
    plumbing: 'Plumbing',
    hvac: 'HVAC',
  };
  return known[lower] ?? titleCase;
}

interface EstimateLineItemsProps {
  items: EstimateItem[];
  estimateNumber: string;
  className?: string;
}

const EstimateLineItems: React.FC<EstimateLineItemsProps> = ({ 
  items, 
  estimateNumber, 
  className = "" 
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatQuantity = (quantity: number, unit: string) => {
    return `${quantity} ${unit}`;
  };

  if (!items || items.length === 0) {
    return (
      <Card className={`remodra-card ${className}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-[var(--dash-accent)] text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Estimate {estimateNumber} - Line Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-[var(--dash-text-muted)]">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium text-[var(--dash-text)]">No line items available</p>
            <p className="text-sm">This estimate doesn't have detailed service breakdown</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalAmount = items.reduce((sum, item) => sum + item.amount, 0);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-[var(--dash-accent)] text-lg flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Estimate {estimateNumber} - Services & Items
        </CardTitle>
        <div className="text-sm text-[var(--dash-text-muted)]">
          Detailed breakdown of services and pricing
        </div>
      </CardHeader>
      <CardContent>
        {/* Mobile: Card layout, Desktop: Table layout */}
        <div className="block sm:hidden space-y-4">
          {items.map((item, index) => (
            <div key={item.id || index} className="border border-[var(--dash-border)] rounded-lg p-4 bg-[var(--dash-bg)]">
              <div className="flex justify-between items-start mb-2">
                <span className="text-[var(--dash-text)] font-medium text-sm">
                  {formatServiceLabel(item)}
                </span>
                <span className="text-[var(--dash-accent)] font-semibold text-lg">
                  {formatCurrency(item.amount)}
                </span>
              </div>
              <p className="text-[var(--dash-text)] text-sm leading-relaxed mb-2 whitespace-pre-line">
                {sanitizeEstimateDescription(item.description)}
              </p>
              <div className="flex justify-between text-sm text-[var(--dash-text-muted)]">
                <span>{formatQuantity(item.quantity, item.unit)}</span>
                <span>@ {formatCurrency(item.unitPrice)}</span>
              </div>
              {getDisplayNotes(item.notes) && (
                <p className="text-[var(--dash-text-muted)] text-xs mt-2 italic bg-[var(--dash-border)]/30 p-2 rounded">
                  Note: {getDisplayNotes(item.notes)}
                </p>
              )}
            </div>
          ))}
        </div>
        
        {/* Desktop: Table layout */}
        <div className="hidden sm:block overflow-x-auto" data-estimate-line-items>
          <Table>
            <TableHeader>
              <TableRow className="border-[var(--dash-border)]">
                <TableHead className="text-[var(--dash-text)] font-semibold">Service</TableHead>
                <TableHead className="text-[var(--dash-text)] font-semibold">Description</TableHead>
                <TableHead className="text-[var(--dash-text)] font-semibold text-right">Quantity</TableHead>
                <TableHead className="text-[var(--dash-text)] font-semibold text-right">Unit Price</TableHead>
                <TableHead className="text-[var(--dash-text)] font-semibold text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => (
                <TableRow 
                  key={item.id || index} 
                  className="border-[var(--dash-border)] estimate-line-items-row transition-colors"
                >
                  <TableCell className="py-3">
                    <span className="text-[var(--dash-text)] font-medium text-sm">
                      {formatServiceLabel(item)}
                    </span>
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="max-w-xs">
                      <p className="text-[var(--dash-text)] text-sm leading-relaxed whitespace-pre-line">
                        {sanitizeEstimateDescription(item.description)}
                      </p>
                      {getDisplayNotes(item.notes) && (
                        <p className="text-[var(--dash-text-muted)] text-xs mt-1 italic">
                          Note: {getDisplayNotes(item.notes)}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-3 text-right">
                    <span className="text-[var(--dash-text)] font-medium">
                      {formatQuantity(item.quantity, item.unit)}
                    </span>
                  </TableCell>
                  <TableCell className="py-3 text-right">
                    <span className="text-[var(--dash-text)]">
                      {formatCurrency(item.unitPrice)}
                    </span>
                  </TableCell>
                  <TableCell className="py-3 text-right">
                    <span className="text-[var(--dash-accent)] font-semibold">
                      {formatCurrency(item.amount)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        
        {/* Summary */}
        <div className="mt-6 pt-4 border-t border-[var(--dash-border)]">
          <div className="flex justify-end">
            <div className="w-48 space-y-2">
              <div className="flex justify-between text-[var(--dash-text)]">
                <span>Subtotal:</span>
                <span>{formatCurrency(totalAmount)}</span>
              </div>
              <div className="flex justify-between text-[var(--dash-accent)] font-semibold text-lg">
                <span>Total:</span>
                <span>{formatCurrency(totalAmount)}</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EstimateLineItems; 